import { defineComponent as _defineComponent } from 'vue'
import { computed, provide, ref, watch } from 'vue';

import {
	FlowRun,
	FlowRunEvents,
	FlowRunStatuses,
	FlowRunData,
	FlowRunState,
	NodeRunState,
	NodeRunStatuses,
	RenderableWidgetEventData,
} from '@ply-io/runner';

import DesignTileMenu from './DesignTileMenu.vue';
import FlowRunContent from './FlowRunContent.vue';
import TileActions from './TileActions.vue';
import TileDropdownMenu from './TileDropdownMenu.vue';
import TileItemChildren from './TileItemChildren.vue';
import {
	FlowRunErrorAssetsKey,
	FlowRunAssetsKey,
	ProgressBarMenuKey,
	Tile,
	TileWithWorkflow,
	isFlowRunSettings,
	TILE_ID_ADD_ON,
} from './types';
import {
	WorkflowsKey,
	ExpansionKey,
	WidgetContentKey,
	MappedFields,
	NodeRunErrorDialogKey,
	TileContextMenuFunctionsInterface,
	TileContextMenuFunctionsKey,
} from '../types';
import api from '@/api';
import { FLOW_RUNNER_END_RUN_ACTION_TYPES } from '@/components/BlockEditorWidget/FieldSettings/EndRunButtonsFieldSettings/end-run-field.const';
import { getTileTitle, useInject } from '@/components/SideBar/composables';
import DropdownRotatingArrow from '@/components/common/RotatingArrow/DropdownRotatingArrow.vue';
import { useRedirectToLogin } from '@/composables';
import { useMainGetters, useNodesGetters, useUiElementsActions, useWorkflowsActions } from '@/store-composables';
import * as WorkflowNodeService from '@/utils/workflow-node-service';
import { ERROR_CODES } from '@/utils/error-handling-service';
import { highlight, destroy as destroyHighlight } from '@/utils/highlight-service';
import { MAPPED_FIELDS_KEY } from '@/utils/workflow-node.const';
import { buildStatusLabel, handleDoneNodeRun } from '@/views/FlowRun/flow-run-service';
import { ERROR_DATA_KEY } from '@/views/FlowRun/flow-run.const';
import { useRunner } from '@/views/SideBar/composables/runner';

type TileChildren = NonNullable<Tile['children']>;
type TileOfAddOns = Omit<Tile, 'children'> & { children: TileChildren };
type NodeRunId = string;

export default /*#__PURE__*/_defineComponent({
  __name: 'TileItem',
  props: {
    tile: null,
    creation: { type: Boolean, default: false },
    disabled: { type: Boolean, default: false },
    isCreatingWorkflow: { type: Boolean, default: false },
    topSeparator: { type: Boolean, default: true },
    bottomSeparator: { type: Boolean, default: true }
  },
  emits: ['click'],
  setup(__props: any, { emit }) {

const props = __props as { tile: Tile, creation: boolean, disabled: boolean, isCreatingWorkflow: boolean, topSeparator: boolean, bottomSeparator: boolean }

const { runWorkflow } = useRunner(api.runnerApi);

const { getMappedFields, styleEditingWorkflowId, clearStyleEditingWorkflowId } = useInject(WorkflowsKey);
const editTitleMenu = computed({
	get: () => styleEditingWorkflowId.value?.id === props.tile.id,
	set: (newV) => {
		// make sure to clear the styleEditingWorkflowId if the value is set to false, only if the editTitleMenu.value is true (means the current tile is being edited)
		if (!newV && editTitleMenu.value) {
			destroyHighlight();
			clearStyleEditingWorkflowId();
		}
	},
});
const afterCreation = computed(() => editTitleMenu.value && !!styleEditingWorkflowId.value?.onCreation);
watch(
	() => editTitleMenu.value,
	(newV) => {
		// if (newV) {
		// 	highlight(`#tit-${props.tile.id}`);
		// }
		if (newV) {
			tempTileTitle.value = tileTitle.value;
			highlight({
				element: `#${tileElementId.value}`,
				onDeselected: () => {
					editTitleMenu.value = false;
				},
			});
		} else {
			tempTileTitle.value = null;
		}
	}
);


const tileElementId = computed(() => `tit-${props.tile.id}`);
const DRIVER_ACTIVE_ELEMENT_CLASS_NAME = 'driver-active-element';
const driverActiveElementClass = computed(() => `${DRIVER_ACTIVE_ELEMENT_CLASS_NAME}-${props.tile.id}`);

/**
 * This function returns all the elements that should be considered as part of the tile, so that we can close the menu when clicking outside of it
 */
const getAllChildElements = () =>
	Array.from(
		document.querySelectorAll(
			`#${tileElementId.value}, #${tileElementId.value} *, .${driverActiveElementClass.value}, .${driverActiveElementClass.value} *, .tile-dropdown-menu-container, .tile-dropdown-menu-container *`
		)
	);
const onClickOutside = () => {
	editTitleMenu.value = false;
};

const isTileOfAddOns = (tile: Tile): tile is TileOfAddOns => tile.id === TILE_ID_ADD_ON;

// `_showRunner` should not be accessed directly, only through `showRunner` computed
const _showRunner = ref<boolean>(false);
// we have a designated WritableComputedRef, because we want to do some extra logic when the value is set
const showRunner = computed({
	get: () => _showRunner.value,
	set: (newV) => {
		if (!newV && endRunData.value && !hasEndRunInterface.value) {
			/**
			 * if the user is closing the tile, and the flow has ended,
			 * and there is no end run interface, we clear the flow run assets
			 */
			initFlowRunAssets();
			clearFlowRun();
		}
		_showRunner.value = newV;
	},
});

const flowRun = ref<FlowRun | null>(null);
const flowRunWasInitiated = computed<boolean>(() => flowRun.value !== null);
const userIsOwner = computed(() => !!flowRun.value?.userIsOwner);
const userInOrganization = computed(() => !!flowRun.value?.userInOrganization);
const isActivelyRunningNodeRun = (nodeRun: NodeRunState) =>
	[NodeRunStatuses.Resumed, NodeRunStatuses.Pending, NodeRunStatuses.Throttled, NodeRunStatuses.Started].includes(nodeRun.status);
// const failedNodeRun = computed(() => flowRun.value?.failedNodeRun);
// const failedNodeRunErrorData = computed(() => failedNodeRun.value?.data?.output.$errorData);
const isNodeRunActivelyRunning = computed<boolean>(() => Object.values(nodeRunsState.value).some(isActivelyRunningNodeRun));
const showItemLoader = computed<boolean>(() => (isNodeRunActivelyRunning.value && !showWidget.value) || widgetAction.value);
const isFinalized = ref(false);
const canExpand = computed<boolean>(() => !isFinalized.value);
watch(
	() => canExpand.value,
	(newV) => {
		if (!newV) {
			// shrink tile if it was expanded and no longer can be expanded
			isExpanded.value = false;
		}
	}
);

const showBottomLoader = computed<boolean>(
	() => (isNodeRunActivelyRunning.value && !showWidget.value) || widgetAction.value || props.isCreatingWorkflow
);
const showBottomSeparator = computed<boolean>(() => props.bottomSeparator && !showBottomLoader.value && !isExpanded.value);
const showTopSeparator = computed<boolean>(() => props.topSeparator && showBottomSeparator.value);
const flowRunStatus = ref<FlowRunStatuses>(FlowRunStatuses.Pending);
const flowRunProgress = ref<number>(0);
const endRunData = ref<FlowRunData | null>(null);
const nodeRunsState = ref<Record<NodeRunId, NodeRunState>>({});
const initFlowRunAssets = () => {
	flowRunStatus.value = FlowRunStatuses.Pending;
	flowRunProgress.value = 0;
	nodeRunsState.value = {};
	flowRunExecutionFailure.value = null;
	isFinalized.value = false;
	isExpanded.value = false;
	endRunData.value = null;
};
const { redirectToLogin } = useRedirectToLogin();
const flowRunExecutionFailure = ref<{ code: unknown; msg: unknown } | null>(null);
const widgetValues = ref<Record<string, unknown>>({});
const showWidget = ref<boolean>(false);
const handleValueUpdate = (data: Record<string, unknown>) => {
	let { value } = data;
	if (typeof value === 'object' && value !== null && 'value' in value && value?.value) {
		// Hack
		value = value.value;
	}
	widgetValues.value[String(data.id)] = value;
};
const widgetToRender = ref<Record<string, unknown> | null>(null);
const clearWidgetAssets = () => {
	widgetValues.value = {};
	widgetToRender.value = null;
	showWidget.value = false;
};
const widgetAction = ref<boolean>(false);

const handleWidgetNode = ({ widgetId, widgetData, nodeRunIdPromise, submitCallback, cancelCallback }: RenderableWidgetEventData) => {
	widgetValues.value = {};
	widgetToRender.value = {
		widget: {
			...widgetData.output,
			widgetID: widgetId,
			nodeRunIdPromise,
			previousSteps: widgetData.previousSteps,
			webhookURL: null, // legacy,
			continueWebhookURL: null, // legacy,
		},
		callbacks: {
			valueUpdate: handleValueUpdate,
			doneClicked: async () => {
				showRunner.value = false;
				widgetAction.value = true;
				try {
					await submitCallback({ values: widgetValues.value });
				} catch (e) {
					showRunner.value = true;
					throw e;
				} finally {
					widgetAction.value = false;
				}
				clearWidgetAssets();
				if (endRunData.value) {
					initFlowRunAssets();
					clearFlowRun();
				}
			},
			cancelClicked: async () => {
				showRunner.value = false;
				try {
					widgetAction.value = true;
					await cancelCallback();
				} catch (e) {
					showRunner.value = true;
					throw e;
				} finally {
					widgetAction.value = false;
				}
				clearWidgetAssets();
				if (endRunData.value) {
					initFlowRunAssets();
					clearFlowRun();
				}
			},
		},
		api: { queryAC: api.queryAC, getWidgetFieldUploadUrl: api.getWidgetFieldUploadUrl },
	};
	showWidget.value = true;
	showRunner.value = true;
};
provide(WidgetContentKey, {
	showWidget: computed(() => showWidget.value),
	widgetToRender: computed(() => widgetToRender.value),
	handleWidgetNode,
});

const isTileWithWorkflow = (tile: Tile): tile is TileWithWorkflow => tile.hasOwnProperty('workflow');
const { appURL } = useMainGetters();
const hasEndRunInterface = computed(
	() =>
		endRunData.value?.output &&
		// @ts-ignore
		WorkflowNodeService.getHiddenFieldsFromTestResult(endRunData.value.output)['action'] ===
			FLOW_RUNNER_END_RUN_ACTION_TYPES.DISPLAY_INTERFACE
);
const startRunWorkflow = async () => {
	const mappedFields: MappedFields = await getMappedFields({
		id: tileWithWorkflow.value.context.id,
		key: tileWithWorkflow.value.context.key,
	});
	initFlowRunAssets();
	try {
		flowRun.value = await runWorkflow(
			tileWithWorkflow.value.workflow.hash,
			{ [MAPPED_FIELDS_KEY]: mappedFields },
			{
				[FlowRunEvents.StateChange]: ({ status, nodeRuns, progress }: FlowRunState) => {
					nodeRunsState.value = nodeRuns;
					flowRunStatus.value = status;
					flowRunProgress.value = progress;
				},
				[FlowRunEvents.Done]: async (payload: { data: Promise<FlowRunData | null> }) => {
					endRunData.value = (await payload.data) || { output: {}, previousSteps: {} };
				},
				[FlowRunEvents.RenderableWidget]: (eventData: RenderableWidgetEventData) => {
					handleWidgetNode(eventData);
				},
				[FlowRunEvents.NodeRunDone]: (nodeRunState: NodeRunState) => {
					handleDoneNodeRun(nodeRunState);
				},
				[FlowRunEvents.Failed]: () => {
					onFailure();
				},
				[FlowRunEvents.Finalized]: () => {
					if ([FlowRunStatuses.Failed, FlowRunStatuses.Done].includes(flowRunStatus.value)) {
						isFinalized.value = true;
						showRunner.value = true;
					} else {
						/**
						 * if the flow was finalized, but the status is not failed or done,
						 * it means that the user has canceled the flow run, so we clear the flow run assets
						 */
						initFlowRunAssets();
						clearFlowRun();
					}
				},
			}
		);
		try {
			await flowRun.value.run();
		} catch (e: unknown) {
			if (e instanceof Error && 'code' in e && (e.code === ERROR_CODES.NOT_AUTHENTICATED || e.code === ERROR_CODES.TOKEN_INVALID)) {
				redirectToLogin(appURL.value);
			} else {
				throw e;
			}
		}
	} catch (e: unknown) {
		if (e instanceof Error) {
			console.error(e);
			const code = 'code' in e ? e.code : undefined;
			flowRunExecutionFailure.value = { msg: e.message, code };
			onFailure();
			flowRunStatus.value = FlowRunStatuses.Failed;
		} else {
			throw e;
		}
	}
};
const onFailure = () => {
	showRunner.value = true; // open the tile if it was closed, to see the error details
	isFailed.value = true;
};
// const flowId = computed<string>(() => flowRun.value?.flowId);
const restart = () => {
	if (isFinalized.value) {
		clearWidgetAssets();
		initFlowRunAssets();
		startRunWorkflow();
	}
};
provide(FlowRunAssetsKey, {
	nodeRuns: computed(() => Object.values(nodeRunsState.value)),
	percentCompleted: computed(() => (isFinalized.value && !!endRunData.value ? 100 : flowRunProgress.value * 100)),
	status: computed(() => flowRunStatus.value),
	text: computed(() => buildStatusLabel(flowRunStatus.value)),
	restart,
	isFinalized: computed(() => isFinalized.value),
	endRunData: computed(() => endRunData.value),
});

const isFailed = ref<boolean>(false);

const CREATOR_WAS_NOTIFIED_MSG = 'The module creator was notified.';
const flowSettings = computed(() => {
	if (flowRun.value) {
		if (!isFlowRunSettings(flowRun.value.settings)) {
			throw new Error('TileItem: flowSettings is not available when flowRun.settings is not available');
		}
		return flowRun.value.settings;
	}
	return null;
});
const canSeeDetails = computed(() => {
	if (userIsOwner.value) {
		return true;
	}
	if (userInOrganization.value) {
		return !(flowSettings.value?.should_hide_errors_from_organization_users ?? false);
	} else {
		return !(flowSettings.value?.should_hide_errors_from_non_organization_users ?? true);
	}
});
const mainErrorMessage = computed(() => {
	if (flowRunExecutionFailure.value) {
		return flowRunExecutionFailure.value.msg;
	}
	let message = '';
	if (!userIsOwner.value) {
		message = `${CREATOR_WAS_NOTIFIED_MSG}`;
	}
	if (canSeeDetails.value) {
		const failedNodeRunActionTitle = failedNodeRun.value?.actionTitle || '';
		const flowRunMessage = `There was an error in ${failedNodeRunActionTitle}.`;
		message = message ? `${message} ${flowRunMessage}` : `${flowRunMessage}`;
	}
	return message;
});
const { editNodeLink } = useNodesGetters();
const nodeRunErrorDialogState = computed(() => {
	let editStepUrl = '';
	if (failedNodeRun.value?.data?.output[ERROR_DATA_KEY]) {
		const errorOutput = Object(failedNodeRun.value?.data?.output[ERROR_DATA_KEY]); // to solve "// Type '{}' may represent a primitive value, which is not permitted as the right operand of the 'in' operator.ts(2638)"
		const flowId = props.tile.id;
		let nodeId;
		if ('nodeId' in errorOutput) {
			nodeId = errorOutput.nodeId;
		}
		let projectId;
		if ('projectId' in errorOutput) {
			projectId = errorOutput.projectId;
		}

		if (nodeId && projectId) {
			editStepUrl = editNodeLink.value({ projectId, flowId, nodeId });
		}
	}
	return failedNodeRun.value
		? {
				actionTitle: failedNodeRun.value.actionTitle,
				msg: flowRun.value?.errorMsg || '',
				serviceSlug: failedNodeRun.value.serviceSlug,
				editStepUrl,
				dismiss: dismissError,
		  }
		: null;
});

const clearFlowRun = () => {
	flowRun.value = null;
};
const dismissError = () => {
	clearFlowRun();
	showRunner.value = false; // close the tile
};
const { setNodeRunErrorDialogState } = useInject(NodeRunErrorDialogKey);
const showErrorDetails = () => {
	if (!nodeRunErrorDialogState.value) {
		throw new Error('TileItem: showErrorDetails is not available when nodeRunErrorDialogState is not available');
	}
	if (!canSeeDetails.value) {
		throw new Error("we shouldn't be able to see the details");
	}
	setNodeRunErrorDialogState(nodeRunErrorDialogState.value);
};

provide(FlowRunErrorAssetsKey, {
	mainErrorMessage,
	isFailed: computed(() => isFailed.value && !!failedNodeRun.value),
	canSeeDetails,
	dismiss: dismissError,
	showErrorDetails,
});

const onClick = async () => {
	if (isTileOfAddOns(props.tile)) {
		if (props.tile.children.length > 0) {
			showChildren.value = !showChildren.value;
		}
	} else if (props.creation) {
		emit('click');
	} else {
		if (props.tile.isPublished) {
			if (!flowRun.value) {
				await startRunWorkflow();
			} else {
				if (showRunner.value) {
					// about to close the menu, so we shrink the tile
					isExpanded.value = false;
				}
				showRunner.value = !showRunner.value;
			}
		} else {
			alert(`Please publish the flow first ${props.tile.id}`);
		}
	}
};
const isOpen = computed<boolean>(() => showChildren.value || showRunner.value);
const showChildren = ref<boolean>(false);

const { clearExpansion, expandTile, expandedTile } = useInject(ExpansionKey);
const isExpanded = computed<boolean>({
	get: () => expandedTile.value === props.tile.id,
	set: (newV) => {
		if (newV) {
			expandTile(props.tile.id);
			showRunner.value = true;
		} else {
			clearExpansion();
		}
	},
});

const editMenu = ref(false);
const progressBarMenu = ref(false);
provide(ProgressBarMenuKey, {
	progressBarMenu: computed(() => progressBarMenu.value),
	setProgressBarMenu: (v: boolean) => (progressBarMenu.value = v),
});

const tileWithWorkflow = computed<TileWithWorkflow>(() => {
	if (!isTileWithWorkflow(props.tile)) {
		throw new Error('TileItem: tileWithWorkflow is not available for add-on tile');
	}
	return props.tile;
});

const failedNodeRun = computed(() => flowRun.value?.failedNodeRun);
const tileTitle = computed(() => {
	if (isTileOfAddOns(props.tile)) {
		return props.tile.title;
	} else if (props.creation) {
		return '';
	}
	return getTileTitle(tileWithWorkflow.value.workflow);
});
const tempTileTitle = ref<string | null>(null);
const displayTitle = computed(() => (editTitleMenu.value ? tempTileTitle.value : tileTitle.value));
const loadingTileTitleSave = ref(false);
const { editWorkflow } = useInject<TileContextMenuFunctionsInterface>(TileContextMenuFunctionsKey);
const openBuilder = async () => {
	await saveTitlePromise;
	editWorkflow(tileWithWorkflow.value.workflow.id, tileWithWorkflow.value.workflow.project);
};
let saveTitlePromise: Promise<void> | null = null;
const saveTileTitle = (tileTitle: string, workflowTitle: string | null) => {
	loadingTileTitleSave.value = true;
	const uiElement = tileWithWorkflow.value.workflow.current_draft.ui_element;
	const injectionStyle = uiElement.metadata.injectionStyle;
	// @ts-ignore
	injectionStyle.text.value = tileTitle;
	const { updateInjectedElementStyle } = useUiElementsActions();
	const { updateWorkflow } = useWorkflowsActions();
	// we keep the updates promise, to await for it in case openBuilder is called
	saveTitlePromise = (async () => {
		try {
			await updateInjectedElementStyle(uiElement.id, injectionStyle);
			if (workflowTitle) {
				await updateWorkflow(tileWithWorkflow.value.workflow.id, { workflow_title: workflowTitle });
			}
			editTitleMenu.value = false;
		} finally {
			loadingTileTitleSave.value = false;
			saveTitlePromise = null;
		}
	})();
};

return { __sfc: true,runWorkflow, getMappedFields, styleEditingWorkflowId, clearStyleEditingWorkflowId, editTitleMenu, afterCreation, props, tileElementId, DRIVER_ACTIVE_ELEMENT_CLASS_NAME, driverActiveElementClass, getAllChildElements, onClickOutside, emit, isTileOfAddOns, _showRunner, showRunner, flowRun, flowRunWasInitiated, userIsOwner, userInOrganization, isActivelyRunningNodeRun, isNodeRunActivelyRunning, showItemLoader, isFinalized, canExpand, showBottomLoader, showBottomSeparator, showTopSeparator, flowRunStatus, flowRunProgress, endRunData, nodeRunsState, initFlowRunAssets, redirectToLogin, flowRunExecutionFailure, widgetValues, showWidget, handleValueUpdate, widgetToRender, clearWidgetAssets, widgetAction, handleWidgetNode, isTileWithWorkflow, appURL, hasEndRunInterface, startRunWorkflow, onFailure, restart, isFailed, CREATOR_WAS_NOTIFIED_MSG, flowSettings, canSeeDetails, mainErrorMessage, editNodeLink, nodeRunErrorDialogState, clearFlowRun, dismissError, setNodeRunErrorDialogState, showErrorDetails, onClick, isOpen, showChildren, clearExpansion, expandTile, expandedTile, isExpanded, editMenu, progressBarMenu, tileWithWorkflow, failedNodeRun, tileTitle, tempTileTitle, displayTitle, loadingTileTitleSave, editWorkflow, openBuilder, saveTitlePromise, saveTileTitle, DesignTileMenu, FlowRunContent, TileActions, TileDropdownMenu, TileItemChildren, DropdownRotatingArrow }
}

})